(() => {
  const selectionEvent = "vehicle-make-fitment:selection-change";
  const previewParams = ["preview_theme_id", "_ab", "_fd", "_sc"];

  if (customElements.get("make-compatible-products")) {
    return;
  }

  class MakeCompatibleProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.make = {
        id: this.dataset.makeId || "",
        gid: this.dataset.makeGid || "",
        name: this.dataset.makeName || "this make",
      };
      this.sourceUrl = this.dataset.sourceUrl || window.routes?.all_products_url || "/collections/all";
      this.content = this.querySelector("[data-make-products-content]");
      this.status = this.querySelector("[data-make-products-status]");
      this.heading = this.closest(".make-compatible-products-section")?.querySelector("[data-make-products-heading]");
      this.sourceSectionId = "";
      this.requestSequence = 0;
      this.currentSignature = "";
      this.filteredResults = null;
      this.currentSelection = this.normalizeSelection({ make: this.make });

      this.onSelectionChange = this.onSelectionChange.bind(this);
      this.onClick = this.onClick.bind(this);
      document.addEventListener(selectionEvent, this.onSelectionChange);
      this.addEventListener("click", this.onClick);

      this.loadSelection(this.currentSelection);
    }

    disconnectedCallback() {
      document.removeEventListener(selectionEvent, this.onSelectionChange);
      this.removeEventListener("click", this.onClick);
      this.abortController?.abort();
      this.initialized = false;
    }

    onSelectionChange(event) {
      const selection = this.normalizeSelection(event.detail || {});

      if (!selection.make.gid || selection.make.gid !== this.make.gid) {
        return;
      }

      this.loadSelection(selection);
    }

    onClick(event) {
      const retry = event.target.closest("[data-make-products-retry]");
      if (retry) {
        event.preventDefault();
        this.loadSelection(this.currentSelection, { force: true });
        return;
      }

      const clientPageLink = event.target.closest("[data-make-products-client-page]");
      if (clientPageLink) {
        event.preventDefault();
        this.renderFilteredPage(Number(clientPageLink.dataset.makeProductsClientPage || "1"), true);
        return;
      }

      const paginationLink = event.target.closest("[data-make-products-content] .pagination a, [data-make-products-page]");
      if (!paginationLink) return;

      event.preventDefault();
      const pageUrl = new URL(paginationLink.href, window.location.origin);
      const page = Math.max(1, Number(pageUrl.searchParams.get("page") || "1"));
      this.loadSelection(this.currentSelection, { page, focusHeading: true, force: true });
    }

    normalizeSelection(detail) {
      return {
        make: detail.make || this.make,
        model: detail.model || null,
        year: detail.year || null,
        engine: detail.engine || null,
        fitmentGids: Array.from(new Set(Array.isArray(detail.fitmentGids) ? detail.fitmentGids.filter(Boolean) : [])),
        exactFitmentGid: detail.exactFitmentGid || "",
        partial: Boolean(detail.partial),
      };
    }

    async loadSelection(selection, { page = 1, focusHeading = false, force = false } = {}) {
      const requiresCanonicalFiltering = Boolean(selection.year && !selection.exactFitmentGid);
      const resultUrl = this.buildResultUrl(selection, requiresCanonicalFiltering ? 1 : page);
      this.currentSelection = selection;

      if (!resultUrl) {
        this.abortController?.abort();
        this.currentSignature = "";
        this.renderEmpty(selection);
        return;
      }

      const signature = requiresCanonicalFiltering
        ? `canonical|${resultUrl.searchParams.toString()}|${selection.fitmentGids.join(",")}|partial:${selection.partial}`
        : `server|${resultUrl.searchParams.toString()}|${page}|partial:${selection.partial}`;

      if (!force && requiresCanonicalFiltering && signature === this.currentSignature && this.filteredResults) {
        this.renderFilteredPage(page, focusHeading);
        return;
      }

      if (!force && signature === this.currentSignature) return;

      this.currentSignature = signature;
      this.filteredResults = null;
      this.abortController?.abort();
      this.abortController = new AbortController();
      const requestId = ++this.requestSequence;

      this.setLoading(selection);

      try {
        if (requiresCanonicalFiltering) {
          const fragments = await this.fetchAllResultPages(resultUrl, this.abortController.signal);
          if (requestId !== this.requestSequence) return;

          this.filteredResults = this.filterCanonicalCards(fragments, selection, resultUrl);
          this.renderFilteredPage(page, focusHeading);
          return;
        }

        const fragment = await this.fetchResultFragment(resultUrl, this.abortController.signal);
        if (requestId !== this.requestSequence) return;

        this.content.innerHTML = fragment.innerHTML;
        this.dataset.resultsUrl = resultUrl.toString();
        this.removeAttribute("data-error");
        this.content.setAttribute("aria-busy", "false");

        const count = Number(fragment.dataset.productCount || "0");
        this.setResultStatus(count, selection);
        this.refreshFitmentMarkers();

        if (focusHeading) {
          window.requestAnimationFrame(() => this.heading?.focus());
        }
      } catch (error) {
        if (error.name === "AbortError" || requestId !== this.requestSequence) return;

        console.warn("MakeCompatibleProducts:", error);
        this.renderError(selection, resultUrl);
      }
    }

    async fetchAllResultPages(resultUrl, signal) {
      const firstFragment = await this.fetchResultFragment(resultUrl, signal);
      const totalProducts = Number(firstFragment.dataset.productCount || "0");
      const totalPages = Math.ceil(totalProducts / 24);
      const fragments = [firstFragment];
      const remainingUrls = [];

      for (let page = 2; page <= totalPages; page += 1) {
        const pageUrl = new URL(resultUrl);
        pageUrl.searchParams.set("page", String(page));
        remainingUrls.push(pageUrl);
      }

      let nextRequest = 0;
      const worker = async () => {
        while (nextRequest < remainingUrls.length) {
          if (signal.aborted) throw new DOMException("The request was aborted", "AbortError");
          const resultIndex = nextRequest;
          nextRequest += 1;
          fragments[resultIndex + 1] = await this.fetchResultFragment(remainingUrls[resultIndex], signal);
        }
      };
      const workerCount = Math.min(3, remainingUrls.length);
      await Promise.all(Array.from({ length: workerCount }, () => worker()));

      return fragments;
    }

    async fetchResultFragment(resultUrl, signal) {
      const requestUrl = new URL(resultUrl);
      requestUrl.searchParams.set("view", "make-products");
      if (this.sourceSectionId) {
        requestUrl.searchParams.set("section_id", this.sourceSectionId);
      }

      const response = await fetch(requestUrl, {
        credentials: "same-origin",
        headers: { Accept: "text/html" },
        signal,
      });

      if (!response.ok) {
        throw new Error(`Product results request failed with ${response.status}`);
      }

      const responseText = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseText, "text/html");
      const fragment = responseDocument.querySelector("[data-make-products-fragment]");
      if (!fragment) {
        throw new Error("Product results response did not include a results fragment");
      }

      this.sourceSectionId = fragment.dataset.sourceSectionId || this.sourceSectionId;
      return fragment;
    }

    filterCanonicalCards(fragments, selection, resultUrl) {
      const allowedIds = new Set(selection.fitmentGids.map((gid) => this.metaobjectId(gid)).filter(Boolean));
      const cards = [];
      const productIds = new Set();
      let gridClassName = "product-grid grid grid-2-col grid-3-col-tablet grid-4-col-desktop";

      fragments.forEach((fragment) => {
        const grid = fragment.querySelector("#product-grid");
        if (grid?.className) gridClassName = grid.className;

        fragment.querySelectorAll("#product-grid > .grid-item").forEach((item) => {
          const matchesSelection = Array.from(item.querySelectorAll("[data-fitment-id]")).some((marker) => {
            return allowedIds.has(String(marker.dataset.fitmentId || ""));
          });
          if (!matchesSelection) return;

          const card = item.querySelector("product-card");
          const productId = card?.dataset.productId || item.querySelector("a[href]")?.getAttribute("href") || item.outerHTML;
          if (productIds.has(productId)) return;

          productIds.add(productId);
          cards.push(item.outerHTML);
        });
      });

      return {
        cards,
        gridClassName,
        resultUrl: resultUrl.toString(),
        selection,
      };
    }

    renderFilteredPage(page = 1, focusHeading = false) {
      if (!this.filteredResults) return;

      const { cards, gridClassName, resultUrl, selection } = this.filteredResults;
      const totalPages = Math.max(1, Math.ceil(cards.length / 24));
      const currentPage = Math.min(Math.max(1, Number(page) || 1), totalPages);
      const pageCards = cards.slice((currentPage - 1) * 24, currentPage * 24);

      this.content.innerHTML = "";
      if (!cards.length) {
        const empty = document.createElement("div");
        empty.className = "make-products-empty";
        empty.textContent = this.dataset.emptyText;
        this.content.append(empty);
      } else {
        const grid = document.createElement("ul");
        grid.id = "product-grid";
        grid.className = gridClassName;
        grid.innerHTML = pageCards.join("");
        this.content.append(grid);

        if (totalPages > 1) {
          this.content.append(this.createClientPagination(currentPage, totalPages, resultUrl));
        }
      }

      this.dataset.resultsUrl = resultUrl;
      this.removeAttribute("data-error");
      this.content.setAttribute("aria-busy", "false");
      this.setResultStatus(cards.length, selection);
      this.refreshFitmentMarkers();

      if (focusHeading) {
        window.requestAnimationFrame(() => this.heading?.focus());
      }
    }

    createClientPagination(currentPage, totalPages, resultUrl) {
      const wrapper = document.createElement("div");
      wrapper.dataset.makeProductsPagination = "";
      const pagination = document.createElement("div");
      pagination.className = "pagination";

      if (currentPage > 1) {
        pagination.append(this.createClientPageLink(
          currentPage - 1,
          resultUrl,
          this.dataset.paginationPrevious || "Previous page",
          "‹",
        ));
      }

      for (let page = 1; page <= totalPages; page += 1) {
        if (page === currentPage) {
          const current = document.createElement("span");
          current.className = "page-number page-number-active";
          current.textContent = String(page);
          current.setAttribute("aria-current", "page");
          pagination.append(current);
        } else {
          const pageLabel = this.interpolate(this.dataset.paginationPageTemplate || "Page [page]", { page });
          pagination.append(this.createClientPageLink(page, resultUrl, pageLabel, String(page)));
        }
      }

      if (currentPage < totalPages) {
        pagination.append(this.createClientPageLink(
          currentPage + 1,
          resultUrl,
          this.dataset.paginationNext || "Next page",
          "›",
        ));
      }

      wrapper.append(pagination);
      return wrapper;
    }

    createClientPageLink(page, resultUrl, label, text) {
      const link = document.createElement("a");
      const url = new URL(resultUrl);
      url.searchParams.set("page", String(page));
      link.href = url.toString();
      link.className = "page-number";
      link.dataset.makeProductsClientPage = String(page);
      link.setAttribute("aria-label", label);
      link.textContent = text;
      return link;
    }

    refreshFitmentMarkers() {
      window.queueMicrotask(() => {
        window.vehicleFitmentController?.updateProductCardFitmentMarkers?.();
      });
    }

    metaobjectId(gid) {
      const value = String(gid || "");
      return decodeURIComponent(value.slice(value.lastIndexOf("/") + 1));
    }

    buildResultUrl(selection, page = 1) {
      const url = new URL(this.sourceUrl, window.requestShopUrl || window.location.origin);
      previewParams.forEach((param) => {
        const value = new URL(window.location.href).searchParams.get(param);
        if (value) url.searchParams.set(param, value);
      });

      url.searchParams.delete("filter.p.m.fitment.make");
      url.searchParams.delete("filter.p.m.fitment.model");
      url.searchParams.delete("filter.p.m.fitment.year");
      url.searchParams.delete("filter.p.m.fitment.engine");
      url.searchParams.delete("filter.p.m.custom.vehicle_fitment");
      url.searchParams.delete("page");
      url.searchParams.delete("section_id");
      url.searchParams.delete("view");

      if (selection.exactFitmentGid) {
        url.searchParams.set("filter.p.m.custom.vehicle_fitment", selection.exactFitmentGid);
      } else {
        url.searchParams.set("filter.p.m.fitment.make", selection.make.gid || this.make.gid);
        if (selection.model?.gid) {
          url.searchParams.set("filter.p.m.fitment.model", selection.model.gid);
        }
        if (selection.year?.gid) {
          if (!selection.fitmentGids.length) return null;
          url.searchParams.set("filter.p.m.fitment.year", selection.year.gid);
        }
        if (selection.engine?.gid) {
          url.searchParams.set("filter.p.m.fitment.engine", selection.engine.gid);
        }
      }

      if (page > 1) url.searchParams.set("page", String(page));
      return url;
    }

    setLoading(selection) {
      this.content.setAttribute("aria-busy", "true");
      this.removeAttribute("data-error");
      this.content.innerHTML = `
        <div class="make-products-loading" aria-hidden="true">
          <span></span><span></span><span></span><span></span>
        </div>
      `;
      this.status.textContent = this.interpolate(this.dataset.loadingTemplate, {
        selection: this.selectionLabel(selection),
      });
    }

    setResultStatus(count, selection) {
      const template = count === 1 ? this.dataset.countOneTemplate : this.dataset.countOtherTemplate;
      let message = this.interpolate(template, {
        count,
        selection: this.selectionLabel(selection),
      });

      if (selection.partial && selection.year && !selection.exactFitmentGid) {
        message = `${message} ${this.dataset.partialText}`.trim();
      }

      this.status.textContent = message;
    }

    renderEmpty(selection) {
      this.content.setAttribute("aria-busy", "false");
      this.content.innerHTML = `<div class="make-products-empty">${this.escapeHtml(this.dataset.emptyText)}</div>`;
      this.status.textContent = this.interpolate(this.dataset.countOtherTemplate, {
        count: 0,
        selection: this.selectionLabel(selection),
      });
    }

    renderError(selection, resultUrl) {
      this.content.setAttribute("aria-busy", "false");
      this.setAttribute("data-error", "true");
      this.content.innerHTML = "";

      const error = document.createElement("div");
      error.className = "make-products-error";
      const message = document.createElement("p");
      message.textContent = this.dataset.errorText;
      const actions = document.createElement("div");
      actions.className = "make-products-error__actions";
      const retry = document.createElement("button");
      retry.type = "button";
      retry.className = "button button-secondary";
      retry.dataset.makeProductsRetry = "";
      retry.textContent = this.dataset.retryText;
      const link = document.createElement("a");
      link.className = "button-text-link";
      link.href = resultUrl.toString();
      link.textContent = this.dataset.viewResultsText;

      actions.append(retry, link);
      error.append(message, actions);
      this.content.append(error);
      this.status.textContent = this.interpolate(this.dataset.errorStatusTemplate, {
        selection: this.selectionLabel(selection),
      });
    }

    selectionLabel(selection) {
      return [
        selection.make?.name || this.make.name,
        selection.model?.name,
        selection.year?.name,
        selection.engine?.name && selection.engine.name !== "Any" ? selection.engine.name : "",
      ].filter(Boolean).join(" ");
    }

    interpolate(template, values) {
      return Object.entries(values).reduce((text, [key, value]) => {
        return String(text || "").replaceAll(`[${key}]`, String(value));
      }, template || "");
    }

    escapeHtml(value) {
      const element = document.createElement("span");
      element.textContent = value || "";
      return element.innerHTML;
    }
  }

  customElements.define("make-compatible-products", MakeCompatibleProducts);
})();
